<template>
  <v-container fluid tag="section">
    <v-row justify="center" align="center">
      <v-col cols="12">
        <v-card class="login-main-card" elevation="1" rounded="lg">
          <div class="login-header-section">
            <div class="logo-section text-center">
              <div class="logo-background mx-auto" :style="logoFlatStyle">
                <lord-icon
                  :src="logoIconSrc"
                  trigger="loop"
                  state="loop-cycle"
                  style="width:64px;height:64px"
                  class="logo-image"
                ></lord-icon>
              </div>
              <div class="login-message mt-4">
                <h1 class="login-title">{{ title }}</h1>
                <p class="login-subtitle mb-0">{{ subtitle }}</p>
              </div>
            </div>

            <div class="login-instructions mt-6">
              <v-row>
                <v-col cols="12" sm="6" class="text-center">
                  <v-chip class="info-chip ma-1" outlined :style="chipFlatStyle" small>
                    <v-icon left small :color="themeColorStart">mdi-account</v-icon>
                    {{ usernameHint }}
                  </v-chip>
                </v-col>
                <v-col cols="12" sm="6" class="text-center">
                  <v-chip class="info-chip ma-1" outlined :style="chipFlatStyle" small>
                    <v-icon left small :color="themeColorStart">mdi-lock</v-icon>
                    {{ passwordHint }}
                  </v-chip>
                </v-col>
              </v-row>
              <div class="text-center mt-2" v-if="noticeText">
                <v-chip class="notice-chip" color="orange" outlined small>
                  {{ noticeText }}
                </v-chip>
              </div>
              <div class="text-center mt-2" v-if="thaidNoticeText">
                <v-chip class="thaid-notice-chip" color="primary" outlined small>
                  <v-icon left small>mdi-qrcode-scan</v-icon>
                  {{ thaidNoticeText }}
                </v-chip>
              </div>
            </div>
          </div>

          <v-card-text class="pa-8">
            <div class="text-center login-button-section">
              <v-dialog :value="dialogValue" @input="$emit('update:dialogValue', $event)" persistent max-width="500">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    class="login-btn"
                    :style="btnFlatStyle"
                    x-large
                    rounded
                    elevation="0"
                    v-bind="attrs"
                    v-on="on"
                    block
                  >
                    <v-icon left size="24">mdi-login-variant</v-icon>
                    เข้าสู่ระบบ
                  </v-btn>
                  <slot name="extra-action"></slot>
                </template>

                <v-card class="login-dialog" rounded="lg">
                  <v-card-title class="dialog-header white--text" :style="btnFlatStyle">
                    <div class="dialog-title-content">
                      <v-icon size="28" color="white" class="mr-3">mdi-shield-account</v-icon>
                      <div>
                        <h3 class="mb-1">{{ title }}</h3>
                        <p class="subtitle mb-0">{{ subtitle }}</p>
                      </div>
                    </div>
                  </v-card-title>

                  <v-card-text class="pa-6">
                    <v-alert v-if="lockoutMessage" type="warning" dense outlined class="mb-4">
                      {{ lockoutMessage }}
                    </v-alert>
                    <v-alert v-if="thaidNoticeText" type="info" dense outlined class="mb-4" icon="mdi-qrcode-scan">
                      {{ thaidNoticeText }}
                    </v-alert>
                    <slot></slot>
                  </v-card-text>

                  <v-divider></v-divider>

                  <v-card-actions class="pa-4">
                    <v-btn rounded outlined color="grey" @click="$emit('update:dialogValue', false)" class="cancel-btn">
                      <v-icon left>mdi-close</v-icon>
                      ยกเลิก
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn
                      rounded
                      large
                      :style="btnFlatStyle"
                      class="submit-btn"
                      elevation="0"
                      :loading="loading"
                      :disabled="!!lockoutMessage"
                      @click="$emit('submit')"
                    >
                      <v-icon left>mdi-login-variant</v-icon>
                      เข้าสู่ระบบ
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-dialog>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-snackbar :value="snackbarValue" @input="$emit('update:snackbarValue', $event)" top class="enhanced-snackbar" :timeout="snackbarTimeout">
      <div class="white--text">
        <v-icon color="white" left>mdi-information</v-icon>
        {{ snackbarMsg }}
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="$emit('update:snackbarValue', false)" color="white">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
/**
 * LoginCardShell.vue — frame ที่ใช้ร่วมกันของ 4 ฟอร์ม login (loginCollege/loginuser/loginuser_gov/loginuserEtc)
 * รวม CSS ที่เคยซ้ำกันเกือบ 600 บรรทัด/ไฟล์ไว้ที่เดียว ส่วนที่ต่างกันตามบทบาท (สี, ข้อความ, ไอคอน)
 * ควบคุมผ่าน props ส่วนฟอร์ม/ฟิลด์จริงอยู่ใน default <slot> ของไฟล์ผู้เรียกใช้
 *
 * ดีไซน์: Modern Minimal Clean — ใช้สีทึบเดียว (themeColorStart) ต่อบทบาทเพื่อคงการแยกสีตามบทบาทไว้
 * แต่ไม่ใช้ gradient/glassmorphism/floating-shapes/shimmer แบบเทมเพลตเดิม (themeColorEnd ยังรับ prop
 * ไว้เพื่อไม่ต้องแก้ไฟล์ผู้เรียกใช้ทั้ง 4 ไฟล์ แต่ไม่ถูกใช้ในการ render สีแล้ว)
 */
export default {
  name: "LoginCardShell",
  props: {
    title: { type: String, required: true },
    subtitle: { type: String, default: "" },
    usernameHint: { type: String, required: true },
    passwordHint: { type: String, required: true },
    noticeText: { type: String, default: "" },
    thaidNoticeText: { type: String, default: "" },
    logoIconSrc: { type: String, default: "https://cdn.lordicon.com/vvyxyrur.json" },
    themeColorStart: { type: String, required: true },
    themeColorEnd: { type: String, default: "" },
    dialogValue: { type: Boolean, default: false },
    snackbarValue: { type: Boolean, default: false },
    snackbarMsg: { type: String, default: "" },
    snackbarTimeout: { type: Number, default: 10000 },
    loading: { type: Boolean, default: false },
    lockoutMessage: { type: String, default: "" }
  },
  computed: {
    logoFlatStyle() {
      return { background: this.themeColorStart };
    },
    chipFlatStyle() {
      return { borderColor: `${this.themeColorStart}55`, color: this.themeColorStart };
    },
    btnFlatStyle() {
      return { background: this.themeColorStart, color: "#fff" };
    }
  }
};
</script>

<style scoped>
.login-main-card {
  background: #ffffff !important;
  border: 1px solid rgba(0, 0, 0, 0.06);
}

.login-header-section {
  padding: 40px 20px 20px;
  background: #f7fafc;
}

.logo-background {
  width: 96px;
  height: 96px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
}

.logo-image {
  position: relative;
  z-index: 1;
}

.login-title {
  font-size: 1.9rem;
  font-weight: 700;
  color: #2d3748;
  margin-bottom: 8px;
}

.login-subtitle {
  color: #718096;
  font-size: 1rem;
}

.info-chip {
  background: #ffffff !important;
}

.notice-chip {
  background: rgba(255, 152, 0, 0.08) !important;
}

.login-btn {
  font-weight: 600;
  text-transform: none;
  font-size: 1.1rem;
  padding: 20px 40px;
  border: none !important;
  min-height: 56px;
  transition: filter 0.2s ease;
}

.login-btn:hover {
  filter: brightness(0.92);
}

.login-dialog {
  background: #ffffff !important;
  overflow: hidden;
}

.dialog-title-content {
  display: flex;
  align-items: center;
  width: 100%;
}

.dialog-header h3 {
  font-size: 1.4rem;
  font-weight: 600;
}

.dialog-header .subtitle {
  font-size: 0.88rem;
  opacity: 0.9;
}

.submit-btn {
  font-weight: 600;
  text-transform: none;
  transition: filter 0.2s ease;
}

.submit-btn:hover {
  filter: brightness(0.92);
}

.cancel-btn {
  transition: background 0.2s ease;
}

.cancel-btn:hover {
  background: rgba(0, 0, 0, 0.05) !important;
}

@media (max-width: 960px) {
  .login-title { font-size: 1.6rem; }
  .login-subtitle { font-size: 0.95rem; }
  .logo-background { width: 84px; height: 84px; }
  .login-btn { font-size: 1rem; padding: 16px 32px; }
  .login-header-section { padding: 30px 15px 15px; }
}

@media (max-width: 600px) {
  .login-title { font-size: 1.5rem; }
  .logo-background { width: 76px; height: 76px; }
  .info-chip { font-size: 0.78rem; margin: 2px !important; }
  .login-header-section { padding: 20px 10px 10px; }
  .login-btn { font-size: 0.95rem; min-height: 48px; }
}
</style>
